import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { useProtectedDataLock } from '../context/SensitiveDataLockContext';

const SensitiveDeleteModal = ({
    isOpen,
    onClose,
    onConfirm,
    itemType = 'élément',
    itemName = '',
    deletionConfig = { enabled: true, mode: 'SOFT_DELETE' },
    loading = false
}) => {
    const { isUnlocked, unlock, checkActionProtected, getActionConfig, userExceptionalAuths } = useProtectedDataLock();
    const [reason, setReason] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [selectedMode, setSelectedMode] = useState('SOFT_DELETE');

    const getItemTypeLabel = (type) => {
        const t = String(type || '').toLowerCase();
        if (t === 'client') return 'client';
        if (t === 'dossier') return 'dossier';
        if (t === 'payment' || t === 'paiement') return 'paiement';
        return type || 'élément';
    };

    const itemLabel = getItemTypeLabel(itemType);

    const actionKeyMap = {
        client: 'CLIENT_DELETE',
        dossier: 'DOSSIER_DELETE',
        payment: 'PAYMENT_DELETE',
        paiement: 'PAYMENT_DELETE'
    };
    const targetCode = actionKeyMap[String(itemType || '').toLowerCase()] || 'CLIENT_DELETE';
    const actionConfig = getActionConfig(targetCode);

    // Extraction de l'ID utilisateur connecté
    const currentUserId = useMemo(() => {
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const u = JSON.parse(userStr);
                return String(u._id || u.id || '');
            }
            const token = localStorage.getItem('token');
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                return String(payload.id || payload._id || '');
            }
        } catch {
            return '';
        }
        return '';
    }, []);

    const userRole = useMemo(() => {
        try {
            const userStr = localStorage.getItem('user');
            if (userStr) return JSON.parse(userStr).typeUser || JSON.parse(userStr).role || '';
            const token = localStorage.getItem('token');
            if (token) return JSON.parse(atob(token.split('.')[1])).typeUser || '';
        } catch {
            return '';
        }
        return '';
    }, []);

    // Recherche d'une autorisation exceptionnelle active correspondante
    const matchingExceptionalAuth = useMemo(() => {
        const now = new Date();
        return (userExceptionalAuths || []).find(auth => {
            if (!auth.isActive) return false;
            if (auth.startAt && new Date(auth.startAt) > now) return false;
            if (!auth.permanent && auth.expiresAt && new Date(auth.expiresAt) <= now) return false;

            const authUserId = String(auth.userId?._id || auth.userId?.id || auth.userId || '');
            if (currentUserId && authUserId && authUserId !== currentUserId) return false;

            const hasEmptyScope = (!auth.actionCodes || auth.actionCodes.length === 0) && (!auth.modules || auth.modules.length === 0);
            const hasGlobalWildcard = (Array.isArray(auth.actionCodes) && (auth.actionCodes.includes('*') || auth.actionCodes.includes('ALL'))) ||
                                      (Array.isArray(auth.modules) && (auth.modules.includes('*') || auth.modules.includes('all') || auth.modules.includes('ALL')));

            const targetModuleMap = { CLIENT_DELETE: 'clients', DOSSIER_DELETE: 'dossiers', PAYMENT_DELETE: 'paiements' };
            const moduleName = targetModuleMap[targetCode] || 'systeme';

            const hasCode = Array.isArray(auth.actionCodes) && (
                auth.actionCodes.map(c => String(c).toUpperCase()).includes(targetCode) ||
                auth.actionCodes.map(c => String(c).toUpperCase()).includes('HARD_DELETE_ENABLE')
            );
            const hasModule = Array.isArray(auth.modules) && (
                auth.modules.map(m => String(m).toLowerCase()).includes(moduleName) ||
                auth.modules.map(m => String(m).toLowerCase()).includes('systeme')
            );

            return hasEmptyScope || hasGlobalWildcard || hasCode || hasModule;
        });
    }, [userExceptionalAuths, currentUserId, targetCode]);

    // Modes de suppression autorisés pour cet utilisateur
    const canSoftDelete = useMemo(() => {
        if (actionConfig?.requirePassword === true && !matchingExceptionalAuth) return false;
        if (matchingExceptionalAuth) return true;
        if (deletionConfig.enabled === false || deletionConfig.mode === 'NOT_ALLOWED') return false;
        if (actionConfig?.enabled === false || actionConfig?.deletionMode === 'NOT_ALLOWED') return false;
        return true;
    }, [matchingExceptionalAuth, deletionConfig, actionConfig]);

    const canHardDelete = useMemo(() => {
        if (actionConfig?.requirePassword === true && !matchingExceptionalAuth) return false;
        const isUserAdmin = userRole === 'admin';
        const hasHardDeleteAuth = matchingExceptionalAuth && (
            matchingExceptionalAuth.enableDisabledFeatures ||
            (Array.isArray(matchingExceptionalAuth.actionCodes) && matchingExceptionalAuth.actionCodes.map(c => String(c).toUpperCase()).includes('HARD_DELETE_ENABLE'))
        );
        const isGlobalHardDeleteMode = deletionConfig.mode === 'HARD_DELETE' || actionConfig?.deletionMode === 'HARD_DELETE';

        return (canSoftDelete || hasHardDeleteAuth) && (isUserAdmin || hasHardDeleteAuth || isGlobalHardDeleteMode);
    }, [userRole, matchingExceptionalAuth, deletionConfig, actionConfig, canSoftDelete]);

    const isBlocked = (!canSoftDelete && !canHardDelete) || (actionConfig?.requirePassword === true && !matchingExceptionalAuth);


    useEffect(() => {
        if (isOpen) {
            setReason('');
            setConfirmText('');
            if (canSoftDelete) {
                setSelectedMode('SOFT_DELETE');
            } else if (canHardDelete) {
                setSelectedMode('HARD_DELETE');
            }
        }
    }, [isOpen, canSoftDelete, canHardDelete]);

    if (!isOpen) return null;

    const isHardDelete = selectedMode === 'HARD_DELETE';
    const isConfirmDisabled = isHardDelete && confirmText.trim().toUpperCase() !== 'SUPPRIMER';
    const isActionProtected = checkActionProtected(targetCode) || (isHardDelete && checkActionProtected('HARD_DELETE_ENABLE'));

    const handleConfirmSubmit = async (e) => {
        e.preventDefault();
        if (isBlocked) return;
        if (isHardDelete && confirmText.trim().toUpperCase() !== 'SUPPRIMER') return;

        if (isActionProtected && !isUnlocked) {
            const pwdResult = await Swal.fire({
                title: 'Code secret de sécurité requis',
                text: `L'action « ${actionConfig?.label || itemType} » est protégée. Veuillez saisir votre code secret de sécurité pour continuer.`,
                input: 'password',
                inputPlaceholder: 'Code secret de protection',
                showCancelButton: true,
                confirmButtonText: 'Déverrouiller et valider',
                cancelButtonText: 'Annuler',
                confirmButtonColor: isHardDelete ? '#dc2626' : '#2563eb'
            });

            if (!pwdResult.isConfirmed || !pwdResult.value) return;
            const unlockDuration = actionConfig?.unlockDurationMinutes || 15;
            const ok = await unlock(pwdResult.value, unlockDuration);
            if (!ok) return;
        }

        onConfirm(reason, selectedMode);
    };

    // CAS 1 : Aucune autorisation dérogatoire ni permission standard -> Carte de blocage d'accès
    if (isBlocked) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(6px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '20px'
            }}>
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '24px',
                    width: '100%',
                    maxWidth: '480px',
                    boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.35)',
                    padding: '32px 28px',
                    position: 'relative',
                    boxSizing: 'border-box'
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '24px',
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            padding: '4px',
                            lineHeight: 1
                        }}
                    >
                        ✕
                    </button>

                    <div style={{
                        width: '88px',
                        height: '88px',
                        borderRadius: '50%',
                        border: '3.5px solid #fed7aa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px auto',
                        fontSize: '46px',
                        color: '#f97316',
                        fontWeight: '700'
                    }}>
                        !
                    </div>

                    <h3 style={{
                        textAlign: 'center',
                        color: '#991b1b',
                        fontSize: '1.55rem',
                        fontWeight: 800,
                        margin: '0 0 20px 0',
                        letterSpacing: '-0.01em'
                    }}>
                        Suppression du {itemLabel} bloquée
                    </h3>

                    <div style={{
                        backgroundColor: '#fff1f2',
                        border: '1px solid #ffe4e6',
                        borderRadius: '16px',
                        padding: '18px 20px',
                        display: 'flex',
                        gap: '14px',
                        alignItems: 'flex-start',
                        marginBottom: '16px'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            minWidth: '40px',
                            borderRadius: '12px',
                            backgroundColor: '#ffe4e6',
                            color: '#991b1b',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '22px',
                            fontWeight: 800
                        }}>
                            !
                        </div>
                        <div>
                            <h4 style={{ margin: '0 0 4px 0', color: '#991b1b', fontSize: '1rem', fontWeight: 800 }}>
                                Action sensible
                            </h4>
                            <p style={{ margin: 0, fontSize: '0.88rem', color: '#881337', lineHeight: '1.5', fontWeight: 500 }}>
                                La suppression directe d'un {itemLabel} est désactivée par la politique de sécurité globale et aucune autorisation dérogatoire active n'a été détectée.
                            </p>
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: '#f8fafc',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '14px',
                        padding: '16px 20px',
                        color: '#0f172a',
                        fontSize: '0.94rem',
                        lineHeight: '1.55',
                        fontWeight: 600
                    }}>
                        Veuillez demander une autorisation exceptionnelle auprès d'un Administrateur Système pour déverrouiller cette action.
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '26px' }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '12px 42px',
                                borderRadius: '10px',
                                border: '2px solid #f59e0b',
                                backgroundColor: '#1e3a5f',
                                color: '#ffffff',
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                cursor: 'pointer',
                                boxShadow: '0 4px 14px rgba(30, 58, 95, 0.25)',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Compris
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // CAS 2 : Autorisation accordée (Standard ou Dérogatoire) -> Modale de suppression avec choix du mode
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
        }}>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '540px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                overflow: 'hidden',
                animation: 'modalFadeIn 0.2s ease-out'
            }}>
                {/* ── Entête ── */}
                <div style={{
                    padding: '24px',
                    borderBottom: '1px solid #e2e8f0',
                    backgroundColor: isHardDelete ? '#fef2f2' : '#f0f9ff',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '22px',
                            backgroundColor: isHardDelete ? '#fee2e2' : '#e0f2fe',
                            color: isHardDelete ? '#dc2626' : '#0284c7'
                        }}>
                            {isHardDelete ? '🚨' : '📦'}
                        </div>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#0f172a' }}>
                                {isHardDelete ? `Suppression définitive du ${itemLabel}` : `Archivage du ${itemLabel}`}
                            </h3>
                            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                                {itemName ? `Cible : ${itemName}` : 'Confirmation requise'}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '20px',
                            color: '#94a3b8',
                            cursor: 'pointer',
                            padding: '4px'
                        }}
                    >
                        ✕
                    </button>
                </div>

                {/* ── Corps ── */}
                <div style={{ padding: '24px' }}>
                    <form onSubmit={handleConfirmSubmit}>
                        {matchingExceptionalAuth && (
                            <div style={{
                                backgroundColor: '#dcfce7',
                                border: '1px solid #86efac',
                                color: '#15803d',
                                padding: '8px 14px',
                                borderRadius: '10px',
                                fontSize: '0.82rem',
                                fontWeight: 700,
                                marginBottom: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <span>🛡️</span> Autorisation exceptionnelle active : "{matchingExceptionalAuth.reason}"
                            </div>
                        )}

                        {/* Choix des modes de suppression autorisés */}
                        {canSoftDelete && canHardDelete && (
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                                    Mode de suppression souhaité :
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '12px 14px',
                                        borderRadius: '12px',
                                        border: selectedMode === 'SOFT_DELETE' ? '2px solid #2563eb' : '1.5px solid #cbd5e1',
                                        backgroundColor: selectedMode === 'SOFT_DELETE' ? '#eff6ff' : '#f8fafc',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}>
                                        <input
                                            type="radio"
                                            name="deletionModeSelect"
                                            value="SOFT_DELETE"
                                            checked={selectedMode === 'SOFT_DELETE'}
                                            onChange={() => setSelectedMode('SOFT_DELETE')}
                                        />
                                        <div>
                                            <strong style={{ display: 'block', fontSize: '0.88rem', color: '#1e3a5f' }}>📦 Archiver</strong>
                                            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Données conservées</span>
                                        </div>
                                    </label>

                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '12px 14px',
                                        borderRadius: '12px',
                                        border: selectedMode === 'HARD_DELETE' ? '2px solid #dc2626' : '1.5px solid #cbd5e1',
                                        backgroundColor: selectedMode === 'HARD_DELETE' ? '#fef2f2' : '#f8fafc',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}>
                                        <input
                                            type="radio"
                                            name="deletionModeSelect"
                                            value="HARD_DELETE"
                                            checked={selectedMode === 'HARD_DELETE'}
                                            onChange={() => setSelectedMode('HARD_DELETE')}
                                        />
                                        <div>
                                            <strong style={{ display: 'block', fontSize: '0.88rem', color: '#991b1b' }}>🚨 Définitive</strong>
                                            <span style={{ fontSize: '0.78rem', color: '#7f1d1d' }}>Irréversible en BDD</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        )}

                        {isHardDelete && (
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#ef4444',
                                color: '#ffffff',
                                padding: '6px 12px',
                                borderRadius: '20px',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                marginBottom: '16px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                <span>⚠️</span> Mode sensible — suppression irréversible
                            </div>
                        )}

                        <p style={{ margin: '0 0 16px 0', fontSize: '0.95rem', color: '#334155', lineHeight: '1.5' }}>
                            {isHardDelete ? (
                                <>
                                    Attention : cette action supprimera <strong>définitivement</strong> les données du {itemLabel} {itemName ? <strong>« {itemName} »</strong> : ''} et <strong>ne pourra pas être annulée</strong>.
                                </>
                            ) : (
                                <>
                                    Le {itemLabel} {itemName ? <strong>« {itemName} »</strong> : ''} sera <strong>archivé</strong> et ne sera plus affiché dans les listes actives. Ses données resteront conservées dans la base de données.
                                </>
                            )}
                        </p>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                                Motif de la suppression / justification (optionnel) :
                            </label>
                            <textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Ex: Demande client, doublon, erreur de saisie..."
                                rows="3"
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '0.9rem',
                                    outline: 'none',
                                    resize: 'vertical'
                                }}
                            />
                        </div>

                        {isHardDelete && (
                            <div style={{
                                backgroundColor: '#fff1f2',
                                border: '1px solid #fecdd3',
                                padding: '16px',
                                borderRadius: '10px',
                                marginBottom: '20px'
                            }}>
                                <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#991b1b', marginBottom: '8px' }}>
                                    Pour confirmer la suppression définitive, veuillez saisir le mot : <span style={{ backgroundColor: '#ffffff', padding: '2px 6px', borderRadius: '4px', border: '1px solid #fda4af' }}>SUPPRIMER</span>
                                </label>
                                <input
                                    type="text"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder="Saisissez SUPPRIMER ici"
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        border: '1px solid #f87171',
                                        fontSize: '0.95rem',
                                        fontWeight: 600,
                                        letterSpacing: '1px',
                                        outline: 'none'
                                    }}
                                />
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    backgroundColor: '#ffffff',
                                    color: '#475569',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={loading || isConfirmDisabled}
                                style={{
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: isHardDelete ? '#dc2626' : '#2563eb',
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    cursor: (loading || isConfirmDisabled) ? 'not-allowed' : 'pointer',
                                    opacity: (loading || isConfirmDisabled) ? 0.5 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                {loading ? 'Traitement en cours...' : isHardDelete ? 'Supprimer définitivement' : 'Archiver l\'élément'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SensitiveDeleteModal;
